import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import calendarService from '../../services/calendarService';
import { FaChartBar, FaChevronDown, FaChevronUp, FaClock } from 'react-icons/fa';

const MonthlyStatsHeader = ({ year, month }) => {
  const { theme } = useTheme();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Load stats when year/month changes
  useEffect(() => {
    if (!year || !month) return;
    
    const loadStats = async () => {
      setLoading(true);
      
      try {
        const data = await calendarService.getMonthlyStats(year, month);
        setStats(data);
      } catch (err) {
        console.error('Errore caricamento statistiche mensili:', err);
        setStats(null);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [year, month]);

  if (!year || !month || loading) return null;

  // Filtra utenti con ferie/permessi per le statistiche compatte
  const usersWithStats = stats?.statistichePerUtente?.filter(userStats => userStats.totale.ore > 0) || [];
  
  // Tutti gli utenti per il caso "nessuna statistica"
  const allUsers = stats?.statistichePerUtente || [];
  
  if (allUsers.length === 0) return null;

  // Theme classes
  const headerBg = theme === 'dark' 
    ? 'bg-gray-800/90 border-gray-700' 
    : 'bg-white/90 border-gray-200';
  
  const textColor = theme === 'dark' ? 'text-gray-300' : 'text-gray-600';
  const accentColor = theme === 'dark' ? 'text-indigo-400' : 'text-emerald-600';

  const monthName = stats?.periodo?.nomeItMese || 'Mese';

  return (
    <div className={`${headerBg} backdrop-blur-sm border rounded-lg shadow-sm p-3 mb-4`}>
      {/* Header compatto */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="flex items-center">
            <FaChartBar className={`${accentColor} mr-2`} />
            <span className={`${textColor} text-sm font-medium`}>
              {monthName} - Ferie/Permessi/Malattia
            </span>
          </div>
          
          {/* Statistiche compatte */}
          <div className="flex items-center space-x-4">
            {usersWithStats.length > 0 ? (
              usersWithStats.map(userStats => (
                <div key={userStats.utente.id} className="flex items-center space-x-1">
                  <span className={`${textColor} text-xs`}>
                    {userStats.utente.nome.split(' ')[0]}:
                  </span>
                  <span className={`${accentColor} text-xs font-semibold`}>
                    {userStats.totale.ore}h
                  </span>
                </div>
              ))
            ) : (
              <span className={`${textColor} text-xs italic`}>
                Nessuna feria/permesso questo mese
              </span>
            )}
          </div>
        </div>

        {/* Toggle button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`${textColor} hover:${accentColor} transition-colors p-1`}
        >
          {isExpanded ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
        </button>
      </div>

      {/* Dettagli espansi */}
      {isExpanded && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          {usersWithStats.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {usersWithStats.map(userStats => (
              <div
                key={userStats.utente.id}
                className={`${theme === 'dark' ? 'bg-gray-700/30' : 'bg-gray-50'} rounded-lg p-3`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className={`${accentColor} font-medium text-sm truncate`}>
                      {userStats.utente.nome}
                    </div>
                    <div className={`${textColor} text-xs opacity-70 truncate`}>
                      {userStats.utente.email}
                    </div>
                  </div>
                  <div className="text-right ml-2">
                    <div className={`${textColor} font-semibold text-sm flex items-center`}>
                      <FaClock className="mr-1" size={10} />
                      {userStats.totale.ore}h
                    </div>
                    <div className={`${textColor} text-xs opacity-70`}>
                      {userStats.totale.giorni}gg
                    </div>
                  </div>
                </div>
                
                {/* Breakdown ferie/permessi/malattia */}
                {(userStats.ferie.ore > 0 || userStats.permessi.ore > 0 || (userStats.malattia && userStats.malattia.ore > 0)) && (
                  <div className="flex justify-between mt-2 text-xs">
                    {userStats.ferie.ore > 0 && (
                      <span className={`${textColor} opacity-70`}>
                        🏖️ {userStats.ferie.ore}h
                      </span>
                    )}
                    {userStats.permessi.ore > 0 && (
                      <span className={`${textColor} opacity-70`}>
                        ⏰ {userStats.permessi.ore}h
                      </span>
                    )}
                    {userStats.malattia && userStats.malattia.ore > 0 && (
                      <span className={`${textColor} opacity-70`}>
                        🏥 {userStats.malattia.ore}h
                      </span>
                    )}
                  </div>
                )}
              </div>
              ))}
            </div>
          ) : (
            <div className={`${textColor} text-center py-6`}>
              <div className="text-4xl mb-2">📭</div>
              <p className="text-sm">Nessuna feria o permesso registrato per {monthName.toLowerCase()}</p>
              <p className="text-xs opacity-70 mt-1">
                Gli utenti: {allUsers.map(u => u.utente.nome.split(' ')[0]).join(', ')}
              </p>
            </div>
          )}
          
          {/* Footer info compatto */}
          <div className={`${textColor} text-xs opacity-50 text-center mt-3 pt-2 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            Aggiornato: {new Date(stats.dataGenerazione).toLocaleString('it-IT')}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyStatsHeader;